/**
 * The production {@link DevelopersSource}: `developers.*` over tRPC
 * (`specs/M8-P4-developers-docs-playground.md` §6).
 *
 * Thin by design — every decision (which entries exist, which badge each carries, what the
 * guides say) is the api's, because the reference is generated from the SDL the api serves and
 * a second copy of that logic in the browser would be a second answer.
 */
import { useMemo } from 'react';
import { useTRPCClient } from '../../lib/trpc';
import type {
  DevelopersOverview,
  DevelopersSource,
  Guide,
  PlaygroundCredential,
  PlaygroundSetup,
  ReferenceEntry,
  ReferenceIndex,
} from './source';

/**
 * `useTRPCClient()` throws outside a provider, and these pages are deliberately renderable
 * without one — the unit suite and `/__ui/developers` both pass a fixture source instead. The
 * hook is still called unconditionally (hook order is what React cares about); only its refusal
 * is caught, and a source built from a missing client refuses loudly if anything ever calls it.
 */
function useOptionalTrpcClient(): ReturnType<typeof useTRPCClient> | null {
  try {
    return useTRPCClient();
  } catch {
    return null;
  }
}

export function useTrpcDevelopersSource(): DevelopersSource {
  const maybeClient = useOptionalTrpcClient();
  return useMemo<DevelopersSource>(() => {
    const client = maybeClient;
    if (!client) {
      const refuse = (): never => {
        throw new Error('The developers pages need a tRPC provider or an injected source.');
      };
      return {
        overview: refuse,
        reference: refuse,
        entry: refuse,
        guides: refuse,
        playground: refuse,
        mintCredential: refuse,
        releaseCredential: refuse,
        setPublicDocs: refuse,
      };
    }
    return {
      overview: async () => (await client.developers.overview.query()) as DevelopersOverview,
      reference: async (version) =>
        (await client.developers.reference.query(
          version ? { version: version as never } : undefined,
        )) as ReferenceIndex,
      entry: async (name, version) =>
        (await client.developers.entry.query({ name, ...(version ? { version: version as never } : {}) })) as ReferenceEntry,
      guides: async (version) =>
        (await client.developers.guides.query(
          version ? { version: version as never } : undefined,
        )) as Guide[],
      playground: async () => (await client.developers.playground.query()) as PlaygroundSetup,
      mintCredential: async () =>
        (await client.developers.playgroundToken.mutate()) as PlaygroundCredential,
      releaseCredential: async () => client.developers.releasePlaygroundToken.mutate(),
      setPublicDocs: async (enabled) => client.developers.setPublicDocs.mutate({ enabled }),
    };
  }, [maybeClient]);
}
